import {
	Alert,
	Button,
	Group,
	Loader,
	Paper,
	Select,
	Stack,
	Text,
	TextInput,
	Title,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { listAdminDiscounts } from "@/features/discounts/api/api";
import type { DiscountKind } from "@/features/discounts/api/types";
import {
	createHotelPromo,
	deleteHotelPromo,
	hotelPromoKeys,
	listHotelPromoHotels,
	listHotelPromos,
	updateHotelPromo,
} from "@/features/hotel-promos/api";
import {
	HotelPromoEditorModal,
	type HotelPromoEditorState,
} from "@/features/hotel-promos/hotel-promo-editor";
import type { HotelPromoFormValues } from "@/features/hotel-promos/hotel-promo-schema";
import {
	createHotelPromoBody,
	updateHotelPromoBody,
} from "@/features/hotel-promos/hotel-promo-schema";
import { HotelPromosTable } from "@/features/hotel-promos/hotel-promos-table";
import type { HotelPromo } from "@/features/hotel-promos/types";
import { listCorporatePartners } from "@/features/partners/admin-partners";
import { describeApiErrorSync } from "@/shared/api/error-message";
import { getMutationErrorMessage } from "@/shared/api/errors";
import { ConfirmDialog } from "@/shared/ui/confirm-dialog";

type StatusFilter = "all" | "active" | "inactive";

function categoryOptions(
	offers: { type: DiscountKind; categories: { id: string; name: string }[] }[],
) {
	const map = new Map<string, string>();
	for (const offer of offers) {
		if (offer.type === "FAMILY") {
			continue;
		}
		for (const category of offer.categories) {
			map.set(category.id, category.name);
		}
	}
	return [...map.entries()].map(([value, label]) => ({ value, label }));
}

export function HotelPromosPage() {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const [search, setSearch] = useState("");
	const [debouncedSearch] = useDebouncedValue(search, 300);
	const [status, setStatus] = useState<StatusFilter>("all");
	const [editor, setEditor] = useState<HotelPromoEditorState | null>(null);
	const [pendingDelete, setPendingDelete] = useState<HotelPromo | null>(null);

	const params = useMemo(
		() => ({
			search:
				debouncedSearch.trim().length >= 3 ? debouncedSearch.trim() : undefined,
			isActive: status === "all" ? undefined : status === "active",
		}),
		[debouncedSearch, status],
	);

	const listQuery = useQuery({
		queryKey: hotelPromoKeys.list(params),
		queryFn: () => listHotelPromos(params),
	});
	const hotelsQuery = useQuery({
		queryKey: hotelPromoKeys.hotels,
		queryFn: listHotelPromoHotels,
	});
	const discountsQuery = useQuery({
		queryKey: ["admin-discounts", "hotel-promo-picker"],
		queryFn: () => listAdminDiscounts({ limit: 100 }),
		enabled: editor !== null,
	});
	const partnersQuery = useQuery({
		queryKey: ["admin-corporate-partners", "hotel-promo-picker"],
		queryFn: () => listCorporatePartners({ limit: 100 }),
		enabled: editor !== null,
	});

	const hotelTitles = useMemo(() => {
		const titles: Record<string, string> = {};
		for (const hotel of hotelsQuery.data ?? []) {
			titles[hotel.key] = hotel.title;
		}
		return titles;
	}, [hotelsQuery.data]);

	const invalidate = () =>
		queryClient.invalidateQueries({ queryKey: hotelPromoKeys.all });

	const saveMutation = useMutation({
		mutationFn: (values: HotelPromoFormValues) => {
			if (editor?.mode === "edit" && editor.row) {
				return updateHotelPromo(editor.row.id, updateHotelPromoBody(values));
			}
			return createHotelPromo(createHotelPromoBody(values));
		},
		onSuccess: async () => {
			notifications.show({ color: "green", message: t("hotel.saved") });
			setEditor(null);
			await invalidate();
		},
		onError: async (error) => {
			notifications.show({
				color: "red",
				title: t("hotel.saveFailed"),
				message: await getMutationErrorMessage(error),
			});
		},
	});

	const toggleMutation = useMutation({
		mutationFn: ({ id, next }: { id: string; next: boolean }) =>
			updateHotelPromo(id, { isActive: next }),
		onSuccess: invalidate,
		onError: async (error) => {
			notifications.show({
				color: "red",
				title: t("hotel.saveFailed"),
				message: await getMutationErrorMessage(error),
			});
		},
	});

	const deleteMutation = useMutation({
		mutationFn: deleteHotelPromo,
		onSuccess: async () => {
			setPendingDelete(null);
			notifications.show({ color: "red", message: t("hotel.deleted") });
			await invalidate();
		},
		onError: async (error) => {
			notifications.show({
				color: "red",
				title: t("hotel.deleteFailed"),
				message: await getMutationErrorMessage(error),
			});
		},
	});

	return (
		<Stack gap="md">
			<Group justify="space-between" align="flex-end" wrap="wrap">
				<div>
					<Title order={2}>{t("hotel.title")}</Title>
					<Text c="dimmed" size="sm" maw={640}>
						{t("hotel.subtitle")}
					</Text>
				</div>
				<Button onClick={() => setEditor({ mode: "create" })}>
					{t("hotel.add")}
				</Button>
			</Group>

			<Paper p="md" radius="lg" withBorder>
				<Stack gap="md">
					<Group grow preventGrowOverflow={false} wrap="wrap">
						<TextInput
							label={t("common.search")}
							placeholder={t("hotel.searchPlaceholder")}
							value={search}
							onChange={(event) => setSearch(event.currentTarget.value)}
						/>
						<Select
							label={t("hotel.status")}
							data={[
								{ value: "all", label: t("hotel.statusAll") },
								{ value: "active", label: t("common.active") },
								{ value: "inactive", label: t("common.inactive") },
							]}
							value={status}
							allowDeselect={false}
							onChange={(value) => {
								if (
									value === "all" ||
									value === "active" ||
									value === "inactive"
								) {
									setStatus(value);
								}
							}}
						/>
					</Group>

					{listQuery.isLoading ? (
						<Group justify="center" py="xl">
							<Loader />
						</Group>
					) : null}
					{listQuery.error ? (
						<Alert color="red">{describeApiErrorSync(listQuery.error)}</Alert>
					) : null}
					{listQuery.data?.length === 0 ? (
						<Text c="dimmed" ta="center" py="xl">
							{t("hotel.empty")}
						</Text>
					) : null}
					{listQuery.data && listQuery.data.length > 0 ? (
						<HotelPromosTable
							rows={listQuery.data}
							hotelTitles={hotelTitles}
							pendingId={
								toggleMutation.isPending
									? toggleMutation.variables?.id
									: undefined
							}
							onEdit={(row) => setEditor({ mode: "edit", row })}
							onToggle={(row, next) =>
								toggleMutation.mutate({ id: row.id, next })
							}
							onDelete={setPendingDelete}
						/>
					) : null}
				</Stack>
			</Paper>

			<HotelPromoEditorModal
				editor={editor}
				saving={saveMutation.isPending}
				hotels={hotelsQuery.data ?? []}
				categories={categoryOptions(discountsQuery.data?.data ?? [])}
				partners={(partnersQuery.data?.data ?? []).map((partner) => ({
					value: partner.id,
					label: partner.name,
				}))}
				hotelsError={
					hotelsQuery.error
						? describeApiErrorSync(hotelsQuery.error)
						: undefined
				}
				onClose={() => {
					if (!saveMutation.isPending) {
						setEditor(null);
					}
				}}
				onSave={(values) => saveMutation.mutate(values)}
			/>
			<ConfirmDialog
				opened={pendingDelete !== null}
				title={t("hotel.confirmDeleteTitle")}
				body={t("hotel.confirmDeleteBody", { code: pendingDelete?.code ?? "" })}
				confirmLabel={t("common.delete")}
				loading={deleteMutation.isPending}
				onCancel={() => setPendingDelete(null)}
				onConfirm={() => {
					if (pendingDelete) {
						deleteMutation.mutate(pendingDelete.id);
					}
				}}
			/>
		</Stack>
	);
}
